import React, { useState } from 'react';
import {
  RefreshCw, LayoutGrid, List, FolderPlus, AlertCircle, Inbox,
} from 'lucide-react';
import type { StorageProvider, UnifiedFileItem } from '../../types/storage';
import { useFileExplorer, useFileUpload } from '../../hooks/useStorage';
import { storageApi } from '../../services/storageApi';
import FileBreadcrumb from './FileBreadcrumb';
import FileItem from './FileItem';
import FileUploadZone from './FileUploadZone';
import SearchBar from './SearchBar';

interface FileExplorerProps {
  provider: StorageProvider;
}

export default function FileExplorer({ provider }: FileExplorerProps) {
  const {
    items, breadcrumbs, loading, error, searchQuery,
    setSearchQuery, openFolder, navigateTo, refresh,
  } = useFileExplorer(provider);

  const { uploads, uploadFiles, clearCompleted } = useFileUpload(provider);
  const [view, setView] = useState<'list' | 'grid'>('list');
  const [showUpload, setShowUpload] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [folderError, setFolderError] = useState('');

  const currentFolderId = breadcrumbs[breadcrumbs.length - 1]?.id ?? 'root';

  const handleOpen = (item: UnifiedFileItem) => {
    if (item.isFolder) {
      openFolder(item);
    } else if (item.webUrl) {
      window.open(item.webUrl, '_blank');
    }
  };

  const handleDelete = async (item: UnifiedFileItem) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    try {
      if (provider === 'google') await storageApi.google.deleteFile(item.id);
      refresh();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const handleUpload = (files: File[]) => {
    uploadFiles(files, currentFolderId);
    setShowUpload(false);
    setTimeout(refresh, 2000);
  };

  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;
    setFolderError('');
    try {
      if (provider === 'google') {
        await storageApi.google.createFolder(newFolderName.trim(), currentFolderId !== 'root' ? currentFolderId : undefined);
      } else {
        await storageApi.onedrive.createFolder(newFolderName.trim(), currentFolderId !== 'root' ? currentFolderId : undefined);
      }
      setCreatingFolder(false);
      setNewFolderName('');
      refresh();
    } catch (err: unknown) {
      setFolderError(err instanceof Error ? err.message : 'Failed to create folder');
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50 flex-wrap">
        <div className="flex-1 min-w-0">
          <FileBreadcrumb breadcrumbs={breadcrumbs} onNavigate={navigateTo} />
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={() => setShowUpload((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Upload
          </button>

          <button
            onClick={() => { setCreatingFolder(true); setFolderError(''); }}
            className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-600 transition-colors"
            title="New folder"
          >
            <FolderPlus className="w-4 h-4" />
          </button>

          <button
            onClick={refresh}
            className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-600 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>

          <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setView('list')}
              className={`p-1.5 ${view === 'list' ? 'bg-indigo-100 text-indigo-700' : 'hover:bg-gray-100 text-gray-500'} transition-colors`}
              title="List view"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setView('grid')}
              className={`p-1.5 ${view === 'grid' ? 'bg-indigo-100 text-indigo-700' : 'hover:bg-gray-100 text-gray-500'} transition-colors`}
              title="Grid view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="px-4 py-2 border-b border-gray-100">
        <SearchBar value={searchQuery} onChange={setSearchQuery} />
      </div>

      {/* Upload zone */}
      {showUpload && (
        <div className="px-4 py-3 border-b border-gray-100 bg-blue-50">
          <FileUploadZone
            onFilesSelected={handleUpload}
            uploads={uploads}
            onClearCompleted={clearCompleted}
          />
        </div>
      )}

      {/* New folder form */}
      {creatingFolder && (
        <form
          onSubmit={handleCreateFolder}
          className="flex items-center gap-2 px-4 py-2 border-b border-gray-100 bg-yellow-50"
        >
          <FolderPlus className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <input
            autoFocus
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Folder name"
            className="flex-1 text-sm border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-300"
          />
          <button
            type="submit"
            className="text-sm font-medium text-white bg-indigo-600 px-3 py-1 rounded hover:bg-indigo-700 transition-colors"
          >
            Create
          </button>
          <button
            type="button"
            onClick={() => { setCreatingFolder(false); setNewFolderName(''); }}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>
          {folderError && <span className="text-xs text-red-500">{folderError}</span>}
        </form>
      )}

      {/* List header (list view only) */}
      {view === 'list' && !loading && items.length > 0 && (
        <div className="flex items-center gap-3 px-3 py-1.5 text-xs font-medium text-gray-400 border-b border-gray-100">
          <span className="w-5" />
          <span className="flex-1">Name</span>
          <span className="hidden sm:block w-20 text-right">Size</span>
          <span className="hidden md:block w-28 text-right">Modified</span>
          <span className="w-20" />
        </div>
      )}

      {/* File list */}
      <div className="flex-1 overflow-y-auto">
        {error ? (
          <div className="flex flex-col items-center gap-3 py-12 text-red-500">
            <AlertCircle className="w-8 h-8" />
            <p className="text-sm">{error}</p>
            <button onClick={refresh} className="text-sm underline">Retry</button>
          </div>
        ) : loading ? (
          <div className={`${view === 'grid' ? 'grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 p-4' : 'divide-y divide-gray-50 px-2 py-1'}`}>
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className={`${view === 'grid' ? 'h-24 rounded-xl' : 'h-10 rounded-lg'} bg-gray-100 animate-pulse`}
              />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-gray-400">
            <Inbox className="w-10 h-10" />
            <p className="text-sm">{searchQuery ? 'No results found' : 'This folder is empty'}</p>
          </div>
        ) : view === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 p-4">
            {items.map((item) => (
              <FileItem
                key={item.id}
                item={item}
                onOpen={handleOpen}
                onDelete={provider === 'google' ? handleDelete : undefined}
                view="grid"
              />
            ))}
          </div>
        ) : (
          <div className="divide-y divide-gray-50 px-2 py-1">
            {items.map((item) => (
              <FileItem
                key={item.id}
                item={item}
                onOpen={handleOpen}
                onDelete={provider === 'google' ? handleDelete : undefined}
                view="list"
              />
            ))}
          </div>
        )}
      </div>

      {/* Status bar */}
      {!loading && !error && (
        <div className="px-4 py-1.5 border-t border-gray-100 bg-gray-50 text-xs text-gray-400">
          {items.length} item{items.length !== 1 ? 's' : ''}
          {searchQuery ? ` matching "${searchQuery}"` : ''}
        </div>
      )}
    </div>
  );
}
